import { z } from "zod";

/** Severity drives the banner's color treatment. `info` is the muted default;
 *  `warning` is amber; `critical` uses the destructive token for outages. */
export const SITE_BANNER_SEVERITIES = ["info", "warning", "critical"] as const;
export type SiteBannerSeverity = (typeof SITE_BANNER_SEVERITIES)[number];

/** http(s) absolute or single-slash root-relative only. `(?![/\\])` blocks
 *  protocol-relative `//host` and backslash `/\host` off-site URLs (browsers
 *  normalize `\`→`/`). */
const SAFE_HREF = /^(https?:\/\/|\/(?![/\\]))/;

/** A link href is safe iff it matches SAFE_HREF and contains no control chars,
 *  DEL, or backslash (URL-normalization vectors a browser may rewrite). */
function hrefIsSafe(href: string): boolean {
  if (!SAFE_HREF.test(href)) return false;
  for (let i = 0; i < href.length; i++) {
    const code = href.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f || code === 0x5c) return false;
  }
  return true;
}

/** The shape authored in the PostHog `site-banner` flag's JSON payload. Only
 *  `message` is required; a bad `severity`/`dismissable` falls back rather than
 *  dropping the banner. */
const PayloadSchema = z.object({
  message: z.string().trim().min(1),
  severity: z.enum(SITE_BANNER_SEVERITIES).catch("info"),
  linkHref: z.string().trim().optional(),
  linkText: z.string().trim().optional(),
  // Explicit dismissal id. Omit it and we derive one from the content, so
  // editing the message re-shows a previously dismissed banner.
  id: z.string().trim().min(1).optional(),
  // Hard banners (e.g. a maintenance window) can opt out of dismissal.
  dismissable: z.boolean().catch(true),
});

export interface SiteBanner {
  /** Stable id used for client-side dismissal persistence. */
  id: string;
  message: string;
  severity: SiteBannerSeverity;
  linkHref?: string;
  linkText?: string;
  dismissable: boolean;
}

/** Parse the PostHog flag payload into a {@link SiteBanner}, or `null` when
 *  there's nothing valid to show. Never throws — a malformed payload degrades
 *  to "no banner", not a render error. */
export function parseSiteBanner(raw: unknown): SiteBanner | null {
  const result = PayloadSchema.safeParse(raw);
  if (!result.success) return null;

  const { id, message, severity, linkHref, linkText, dismissable } =
    result.data;
  // A link needs both halves and a safe href; otherwise drop just the link.
  const linkOk = Boolean(linkHref && linkText && hrefIsSafe(linkHref));

  return {
    id: id ?? `${severity}:${message}`,
    message,
    severity,
    linkHref: linkOk ? linkHref : undefined,
    linkText: linkOk ? linkText : undefined,
    dismissable,
  };
}

/** True when the banner's link points at an app store listing: a "get the
 *  app" promo. Inside the native app shell that's noise (the viewer already
 *  has the app), so the banner hides itself there. Keyed on the link, not a
 *  payload flag, so any future app promo is covered with no PostHog edit. */
export function promotesTheApp(banner: Pick<SiteBanner, "linkHref">): boolean {
  if (!banner.linkHref) return false;
  try {
    const host = new URL(banner.linkHref).hostname;
    return (
      host === "apps.apple.com" ||
      host === "itunes.apple.com" ||
      host === "apple.co"
    );
  } catch {
    return false;
  }
}

/** The banner to show: an App Store promo is dropped inside the app shell and where the page hides app promotion. */
export function bannerToShow(
  banner: SiteBanner | null,
  where: { inShell: boolean; hideAppPromotion: boolean },
): SiteBanner | null {
  return banner &&
    (where.inShell || where.hideAppPromotion) &&
    promotesTheApp(banner)
    ? null
    : banner;
}
